import { displayText } from "./gpsrFormUtils";

export const GPSR_PRODUCT_TYPE_CLOTHING_TEXTILE = "clothing_textile";

export const GPSR_PRODUCT_LABEL_CREATE_STATUS = "draft";

export const resolveGpsrProductLabelWriteStatus = (currentLabel) => {
  const normalized = String(currentLabel?.status ?? "")
    .trim()
    .toLowerCase();
  if (normalized === "published") return "published";
  return "draft";
};

export function resolveGpsrProductLabelFile(value) {
  if (typeof File !== "undefined" && value instanceof File) {
    return value;
  }
  if (
    value &&
    typeof value === "object" &&
    typeof File !== "undefined" &&
    value.file instanceof File
  ) {
    return value.file;
  }
  return null;
}

function appendOptionalTrimmed(payload, key, raw) {
  const value = displayText(raw);
  if (value) {
    payload[key] = value;
  }
}

function appendOptionalStringArray(payload, key, raw) {
  if (!Array.isArray(raw)) return;
  const values = raw.map((item) => displayText(item)).filter(Boolean);
  if (values.length === 0) return;
  payload[key] = values;
}

function setUpdateText(payload, key, raw) {
  payload[key] = displayText(raw);
}

function setUpdateNullable(payload, key, raw) {
  const value = displayText(raw);
  payload[key] = value || null;
}

function setUpdateStringArray(payload, key, raw) {
  payload[key] = Array.isArray(raw)
    ? raw.map((item) => displayText(item)).filter(Boolean)
    : [];
}

function appendManufacturerFields(payload, manufacturer = {}) {
  appendOptionalTrimmed(
    payload,
    "manufacturer_company_name",
    manufacturer.companyName
  );
  appendOptionalTrimmed(payload, "business_name", manufacturer.businessName);
  appendOptionalTrimmed(
    payload,
    "manufacturer_address_line",
    manufacturer.streetAddress
  );
  appendOptionalTrimmed(
    payload,
    "manufacturer_postal_code",
    manufacturer.postalCode
  );
  appendOptionalTrimmed(payload, "manufacturer_city", manufacturer.city);
  appendOptionalTrimmed(
    payload,
    "manufacturer_country_id",
    manufacturer.country
  );
  appendOptionalTrimmed(payload, "manufacturer_email", manufacturer.email);
}

function appendManufacturerFieldsForUpdate(payload, manufacturer = {}) {
  setUpdateText(
    payload,
    "manufacturer_company_name",
    manufacturer.companyName
  );
  setUpdateText(payload, "business_name", manufacturer.businessName);
  setUpdateText(
    payload,
    "manufacturer_address_line",
    manufacturer.streetAddress
  );
  setUpdateText(
    payload,
    "manufacturer_postal_code",
    manufacturer.postalCode
  );
  setUpdateText(payload, "manufacturer_city", manufacturer.city);
  setUpdateNullable(
    payload,
    "manufacturer_country_id",
    manufacturer.country
  );
  setUpdateText(payload, "manufacturer_email", manufacturer.email);
}

function clearImporterFields(payload) {
  payload.importer_company_name = "";
  payload.importer_address_line = "";
  payload.importer_postal_code = "";
  payload.importer_city = "";
  payload.importer_country_id = null;
  payload.importer_email = "";
}

function appendImporterFields(payload, importer = {}) {
  if (!importer?.enabled) return;
  appendOptionalTrimmed(
    payload,
    "importer_company_name",
    importer.companyName
  );
  appendOptionalTrimmed(
    payload,
    "importer_address_line",
    importer.streetAddress
  );
  appendOptionalTrimmed(
    payload,
    "importer_postal_code",
    importer.postalCode
  );
  appendOptionalTrimmed(payload, "importer_city", importer.city);
  appendOptionalTrimmed(payload, "importer_country_id", importer.country);
  appendOptionalTrimmed(payload, "importer_email", importer.email);
}

function appendImporterFieldsForUpdate(payload, importer = {}) {
  if (!importer?.enabled) {
    clearImporterFields(payload);
    return;
  }
  setUpdateText(payload, "importer_company_name", importer.companyName);
  setUpdateText(payload, "importer_address_line", importer.streetAddress);
  setUpdateText(payload, "importer_postal_code", importer.postalCode);
  setUpdateText(payload, "importer_city", importer.city);
  setUpdateNullable(payload, "importer_country_id", importer.country);
  setUpdateText(payload, "importer_email", importer.email);
}

function appendSafetyFields(payload, safety = {}, { isTextile = false } = {}) {
  appendOptionalTrimmed(payload, "safety_information", safety.safetyInformation);
  appendOptionalStringArray(
    payload,
    "approved_use_restrictions",
    safety.approvedUseRestrictions
  );
  appendOptionalTrimmed(
    payload,
    "maximum_liquid_temperature",
    safety.maximumLiquidTemperature
  );
  appendOptionalTrimmed(payload, "minimum_user_age", safety.minimumUserAge);
  appendOptionalTrimmed(payload, "other", safety.other);
  if (isTextile) {
    appendOptionalStringArray(payload, "care_symbols", safety.careSymbols);
    appendOptionalTrimmed(
      payload,
      "additional_care_instructions",
      safety.additionalCareInstructions
    );
  }
}

function appendSafetyFieldsForUpdate(
  payload,
  safety = {},
  { isTextile = false } = {}
) {
  setUpdateText(payload, "safety_information", safety.safetyInformation);
  setUpdateStringArray(
    payload,
    "approved_use_restrictions",
    safety.approvedUseRestrictions
  );
  setUpdateNullable(
    payload,
    "maximum_liquid_temperature",
    safety.maximumLiquidTemperature
  );
  setUpdateText(payload, "minimum_user_age", safety.minimumUserAge);
  setUpdateText(payload, "other", safety.other);
  if (isTextile) {
    setUpdateStringArray(payload, "care_symbols", safety.careSymbols);
    setUpdateText(
      payload,
      "additional_care_instructions",
      safety.additionalCareInstructions
    );
  } else {
    payload.care_symbols = [];
    payload.additional_care_instructions = "";
  }
}

export function buildGpsrProductLabelCreatePayload({
  productId,
  traceability = {},
  manufacturer = null,
  importer = null,
  safety = null,
} = {}) {
  const payload = {
    country_specifics_id: productId,
    product_name: displayText(traceability.productName),
    status: GPSR_PRODUCT_LABEL_CREATE_STATUS,
  };

  appendOptionalTrimmed(payload, "batch", traceability.batchLotNumber);
  appendOptionalTrimmed(payload, "size", traceability.size);
  appendOptionalTrimmed(payload, "gross_weight", traceability.grossWeight);
  appendOptionalTrimmed(
    payload,
    "gross_weight_unit",
    traceability.grossWeightUnit
  );
  appendOptionalTrimmed(payload, "product_type", traceability.productType);
  appendOptionalTrimmed(payload, "expiry_date", traceability.expiryDate);
  appendOptionalTrimmed(
    payload,
    "country_of_origin_id",
    traceability.countryOfOrigin
  );
  appendOptionalTrimmed(payload, "barcode", traceability.barcode);
  appendOptionalTrimmed(payload, "colour_variant", traceability.colourVariant);
  appendOptionalTrimmed(payload, "model", traceability.model);
  appendOptionalTrimmed(payload, "gpc_code", traceability.gpcCode);

  if (manufacturer) {
    appendManufacturerFields(payload, manufacturer);
  }

  if (importer) {
    appendImporterFields(payload, importer);
  }

  if (safety) {
    appendSafetyFields(payload, safety, {
      isTextile: displayText(traceability.productType) === GPSR_PRODUCT_TYPE_CLOTHING_TEXTILE,
    });
  }

  return payload;
}

export function buildGpsrProductLabelUpdatePayload({
  productId,
  traceability = {},
  manufacturer = null,
  importer = null,
  safety = null,
  status = "draft",
} = {}) {
  const payload = {
    country_specifics_id: productId,
    product_name: displayText(traceability.productName),
    status: status === "published" ? "published" : "draft",
  };

  setUpdateText(payload, "batch", traceability.batchLotNumber);
  setUpdateText(payload, "size", traceability.size);
  setUpdateText(payload, "product_type", traceability.productType);
  setUpdateText(payload, "barcode", traceability.barcode);
  setUpdateText(payload, "colour_variant", traceability.colourVariant);
  setUpdateText(payload, "model", traceability.model);
  setUpdateText(payload, "gpc_code", traceability.gpcCode);

  setUpdateNullable(payload, "gross_weight", traceability.grossWeight);
  setUpdateNullable(payload, "expiry_date", traceability.expiryDate);
  setUpdateNullable(
    payload,
    "country_of_origin_id",
    traceability.countryOfOrigin
  );

  setUpdateText(
    payload,
    "gross_weight_unit",
    traceability.grossWeightUnit
  );

  if (manufacturer) {
    appendManufacturerFieldsForUpdate(payload, manufacturer);
  }

  if (importer != null) {
    appendImporterFieldsForUpdate(payload, importer);
  }

  if (safety != null) {
    appendSafetyFieldsForUpdate(payload, safety, {
      isTextile:
        displayText(traceability.productType) ===
        GPSR_PRODUCT_TYPE_CLOTHING_TEXTILE,
    });
  }

  return payload;
}

export function hasPendingGpsrProductLabelFiles({
  brandLogoFile,
  productPhotoFile,
  declarationFile,
} = {}) {
  return Boolean(
    resolveGpsrProductLabelFile(brandLogoFile) ||
      resolveGpsrProductLabelFile(productPhotoFile) ||
      resolveGpsrProductLabelFile(declarationFile)
  );
}

export function buildGpsrProductLabelFormData({
  data,
  brandLogoFile,
  productPhotoFile,
  declarationFile,
} = {}) {
  const formData = new FormData();
  formData.append("data", JSON.stringify(data ?? {}));

  const brandLogo = resolveGpsrProductLabelFile(brandLogoFile);
  if (brandLogo) {
    formData.append("label_file", brandLogo);
  }

  const productPhoto = resolveGpsrProductLabelFile(productPhotoFile);
  if (productPhoto) {
    formData.append("photo_product", productPhoto);
  }

  const declaration = resolveGpsrProductLabelFile(declarationFile);
  if (declaration) {
    formData.append("declaration_of_conformity", declaration);
  }

  return formData;
}

export function buildGpsrFibreComponentPayload(component = {}) {
  const componentType = displayText(component.componentType);
  const fibres = (Array.isArray(component.fibres) ? component.fibres : [])
    .map((fibre) => {
      const fibreType = displayText(fibre?.fibreType);
      const percentage = displayText(fibre?.percentage);
      if (!fibreType || !percentage) return null;
      return {
        fibre_type: fibreType,
        percentage,
      };
    })
    .filter(Boolean);

  if (!componentType || fibres.length === 0) {
    return null;
  }

  return {
    component_type: componentType,
    fibres,
  };
}

export function buildGpsrFibreComponentsPayload(list = []) {
  return (Array.isArray(list) ? list : [])
    .map((component) => buildGpsrFibreComponentPayload(component))
    .filter(Boolean);
}

function normalizeGpsrPayloadValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => normalizeGpsrPayloadValue(item));
  }
  return String(value).trim();
}

export function normalizeGpsrProductLabelPayload(payload) {
  if (payload == null || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }
  const normalized = {};
  Object.keys(payload)
    .sort()
    .forEach((key) => {
      const value = payload[key];
      if (value === undefined) return;
      normalized[key] = normalizeGpsrPayloadValue(value);
    });
  return normalized;
}

export function gpsrProductLabelPayloadsEqual(left, right) {
  const a = normalizeGpsrProductLabelPayload(left);
  const b = normalizeGpsrProductLabelPayload(right);
  if (a == null || b == null) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}
